import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Check, MessageCircle } from "lucide-react";
import { Seo, Breadcrumbs, breadcrumbSchema, faqSchema } from "@/components/Seo";
import { Reveal, SectionTag } from "@/components/Motion";
import { ChatButton, openWhatsApp, WA_MESSAGES } from "@/components/Cta";
import FaqAccordion from "@/components/FaqAccordion";
import { LANDING_PAGES } from "@/data/landing";
import { trackEvent } from "@/lib/tracking";
import NotFound from "@/pages/NotFound";

export default function Landing() {
  const { slug } = useParams();
  const lp = LANDING_PAGES[slug];

  useEffect(() => {
    if (lp) {
      trackEvent("ad_landing_view", { landing: slug });
      trackEvent("service_view", { service: lp.service });
    }
  }, [slug, lp]);

  if (!lp) return <NotFound />;

  const crumbs = [{ name: "首页", path: "/" }, { name: lp.kicker, path: `/lp/${lp.slug}` }];

  return (
    <>
      <Seo
        title={lp.seoTitle}
        description={lp.seoDesc}
        path={`/lp/${lp.slug}`}
        noindex
        schemas={[
          breadcrumbSchema(crumbs),
          faqSchema(lp.faqs),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: lp.kicker,
            description: lp.seoDesc,
            provider: { "@id": "https://tkshopmy.com/#organization" },
            areaServed: { "@type": "Country", name: "Malaysia" },
            url: `https://tkshopmy.com/lp/${lp.slug}`,
          },
        ]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-24 md:pt-28 pb-12 md:pb-16 grain" data-testid="lp-hero">
        <div className="pointer-events-none absolute -left-32 top-16 h-80 w-80 rounded-full bg-lime/25 blur-3xl" />
        <div className="mx-auto grid max-w-6xl gap-10 px-5 md:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div>
            <Breadcrumbs items={crumbs} />
            <Reveal y={12}>
              <p className="inline-flex items-center gap-2 rounded-full border border-forest/20 bg-white px-4 py-1.5 text-xs font-semibold tracking-wide text-forest" data-testid="lp-kicker">
                <span className="h-1.5 w-1.5 rounded-full bg-lime" />
                {lp.kicker}
              </p>
              <h1 className="mt-5 font-sans text-3xl font-bold leading-[1.2] tracking-tight text-balance text-ink sm:text-4xl lg:text-[2.9rem]">
                {lp.h1}
                {lp.h1Line2 && (
                  <span className="mt-2 block text-xl font-semibold leading-snug text-ink/75 sm:text-2xl lg:text-3xl">
                    {lp.h1Line2}
                  </span>
                )}
              </h1>
              <p className="mt-5 max-w-lg text-sm leading-relaxed text-ink/65 md:text-base">{lp.sub}</p>
              {lp.subExtra && (
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-ink/55 md:text-base">{lp.subExtra}</p>
              )}
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <ChatButton location={`lp-${slug}-hero`} stage={lp.stage} service={lp.service} variant="primary" testId="lp-hero-chat">
                  {lp.ctaPrimary}
                </ChatButton>
                {lp.ctaSecondary && (
                  <a
                    href="#lp-steps"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-base font-semibold text-ink transition-colors hover:border-forest hover:text-forest"
                    data-testid="lp-hero-secondary"
                  >
                    {lp.ctaSecondary}
                  </a>
                )}
              </div>
              <p className="mt-3.5 text-xs font-medium text-ink/50">{lp.ctaNote}</p>
              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2">
                {lp.trustPoints.map((t) => (
                  <span key={t} className="flex items-center gap-1.5 text-[13px] font-medium text-ink/70">
                    <Check size={14} className="text-moss" />
                    {t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div className="rounded-3xl border border-ink/10 bg-white p-6 shadow-[0_20px_50px_rgba(16,23,17,0.08)] md:p-8" data-testid="lp-includes-card">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-moss">{lp.includesTitle}</p>
              <ul className="mt-5 space-y-3.5">
                {lp.includes.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-relaxed text-ink/75">
                    <Check size={16} className="mt-0.5 shrink-0 text-moss" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHO FOR + STEPS (legacy layout) / EXTENDED SECTIONS (audience-led layout) */}
      {lp.audiences ? (
        <>
          {/* AUDIENCES */}
          <section className="bg-white py-12 md:py-16" data-testid="lp-audience-section">
            <div className="mx-auto max-w-6xl px-5 md:px-8">
              <Reveal>
                <SectionTag>适合谁</SectionTag>
                <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight sm:text-3xl">
                  这些情况都可以先了解
                </h2>
              </Reveal>
              <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {lp.audiences.map((a, i) => (
                  <Reveal key={a.title} delay={i * 0.05}>
                    <div className="h-full rounded-2xl border border-ink/10 bg-paper p-5" data-testid={`lp-audience-card-${i}`}>
                      <p className="flex items-center gap-2 text-sm font-bold text-ink">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-lime" />
                        {a.title}
                      </p>
                      <p className="mt-2 text-sm leading-relaxed text-ink/60">{a.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              {lp.audienceCta && (
                <Reveal delay={0.15} className="mt-8 text-center">
                  <ChatButton location={`lp-${slug}-audience`} stage={lp.stage} service={lp.service} variant="primary" testId="lp-audience-cta">
                    {lp.audienceCta}
                  </ChatButton>
                </Reveal>
              )}
            </div>
          </section>

          {/* ASSIST */}
          <section className="py-12 md:py-16" data-testid="lp-assist-section">
            <div className="mx-auto max-w-6xl px-5 md:px-8">
              <Reveal>
                <SectionTag>我们可以协助什么</SectionTag>
                <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight sm:text-3xl">
                  从准备开始到后续运营
                </h2>
              </Reveal>
              <div className="mt-8 grid gap-4 md:grid-cols-2">
                {lp.assistItems.map((item, i) => (
                  <Reveal key={item.title} delay={i * 0.04}>
                    <div className="flex h-full gap-4 rounded-2xl border border-ink/10 bg-white p-5" data-testid={`lp-assist-item-${i}`}>
                      <span className="font-display text-xl italic text-moss">{String(i + 1).padStart(2, "0")}</span>
                      <div>
                        <p className="text-sm font-bold text-ink">{item.title}</p>
                        <p className="mt-1 text-sm leading-relaxed text-ink/60">{item.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* DROPSHIPPING */}
          {lp.dropship && (
            <section className="bg-white py-12 md:py-16" data-testid="lp-dropship-section">
              <div className="mx-auto grid max-w-6xl gap-10 px-5 md:px-8 lg:grid-cols-2 lg:items-center">
                <Reveal>
                  <SectionTag>一件代发 · 无需自行囤货</SectionTag>
                  <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight sm:text-3xl">
                    {lp.dropship.title}
                  </h2>
                  <p className="mt-5 text-sm leading-relaxed text-ink/65 md:text-base">{lp.dropship.intro1}</p>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65 md:text-base">{lp.dropship.intro2}</p>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65 md:text-base">{lp.dropship.outro}</p>
                  <div className="mt-6 rounded-2xl border border-forest/20 bg-paper p-5" data-testid="lp-dropship-disclaimer">
                    <p className="text-xs leading-relaxed text-ink/60 md:text-sm">{lp.dropship.disclaimer}</p>
                  </div>
                </Reveal>
                <Reveal delay={0.1}>
                  <div className="rounded-3xl border border-ink/10 bg-paper p-6 md:p-8">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-moss">我们会先说明</p>
                    <ul className="mt-5 space-y-3.5">
                      {lp.dropship.items.map((item) => (
                        <li key={item} className="flex gap-3 text-sm leading-relaxed text-ink/75">
                          <Check size={16} className="mt-0.5 shrink-0 text-moss" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              </div>
            </section>
          )}

          {/* STEPS */}
          <section id="lp-steps" className="py-12 md:py-16 scroll-mt-24" data-testid="lp-steps-section">
            <div className="mx-auto max-w-3xl px-5 md:px-8">
              <Reveal>
                <SectionTag>怎样开始</SectionTag>
                <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight sm:text-3xl">
                  {lp.stepsTitle || "三个步骤，先聊清楚再决定"}
                </h2>
              </Reveal>
              <ol className="mt-8 space-y-4">
                {lp.steps.map((s, i) => (
                  <Reveal key={s.title} delay={i * 0.05}>
                    <li className="flex gap-4 rounded-xl border border-ink/10 bg-white px-5 py-4">
                      <span className="font-display text-xl italic text-moss">0{i + 1}</span>
                      <div>
                        <p className="text-sm font-bold text-ink">{s.title}</p>
                        <p className="mt-1 text-sm leading-relaxed text-ink/55">{s.desc}</p>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ol>
              {lp.stepsCta && (
                <Reveal delay={0.15} className="mt-8 text-center">
                  <ChatButton location={`lp-${slug}-steps`} stage={lp.stage} service={lp.service} variant="primary" testId="lp-steps-cta">
                    {lp.stepsCta}
                  </ChatButton>
                </Reveal>
              )}
            </div>
          </section>
        </>
      ) : (
        <section className="bg-white py-12 md:py-16">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 md:px-8 lg:grid-cols-2">
            <Reveal>
              <SectionTag>适合谁</SectionTag>
              <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight sm:text-3xl">
                这项服务适合这些情况
              </h2>
              <ul className="mt-6 space-y-3">
                {lp.whoFor.map((w) => (
                  <li key={w} className="flex items-center gap-3 rounded-xl border border-ink/10 bg-paper px-5 py-3.5 text-sm font-medium text-ink/80">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-lime" />
                    {w}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <SectionTag>怎样开始</SectionTag>
              <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight sm:text-3xl">
                三个步骤，先聊清楚再决定
              </h2>
              <ol className="mt-6 space-y-4">
                {lp.steps.map((s, i) => (
                  <li key={s.title} className="flex gap-4 rounded-xl border border-ink/10 bg-paper px-5 py-4">
                    <span className="font-display text-xl italic text-moss">0{i + 1}</span>
                    <div>
                      <p className="text-sm font-bold text-ink">{s.title}</p>
                      <p className="mt-1 text-sm text-ink/55">{s.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>
        </section>
      )}

      {/* TRUST NOTE */}
      <section className="bg-sand py-10 md:py-12">
        <div className="mx-auto max-w-3xl px-5 text-center md:px-8">
          {lp.opsNote && (
            <div className="mb-8 border-b border-ink/10 pb-8" data-testid="lp-ops-note">
              <p className="flex items-center justify-center gap-2 text-sm font-bold text-forest md:text-base">
                <span className="h-1.5 w-1.5 rounded-full bg-lime" />
                {lp.opsNote.title}
              </p>
              <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-ink/60">
                {lp.opsNote.body}
              </p>
              <button
                onClick={() => openWhatsApp(`lp-${slug}-opsnote`, { text: lp.opsNote.cta, message: WA_MESSAGES.dropshipping })}
                className="mt-3 text-sm font-semibold text-forest underline-offset-4 transition-colors hover:text-ink hover:underline"
                data-testid="lp-opsnote-cta"
                data-location={`lp-${slug}-opsnote`}
              >
                {lp.opsNote.cta} →
              </button>
            </div>
          )}
          <p className="text-sm leading-relaxed text-ink/70 md:text-base">
            我们会根据实际产品和店铺情况提供建议，不使用夸大的销量或收入承诺。
            TikTok Shop 的审核、流量和平台结果由平台最终决定。
          </p>
          <p className="mt-2 text-xs text-ink/45">
            TKShopMY 是独立电商服务提供商，并非 TikTok 或 TikTok Shop 官方机构或关联公司。
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-4xl px-5 md:px-8">
          <Reveal>
            <SectionTag>常见问题</SectionTag>
            <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight sm:text-3xl">
              大家最常问的问题
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-8">
            <FaqAccordion faqs={lp.faqs} testIdPrefix="lp-faq" />
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-forest py-14 text-paper md:py-20 grain" data-testid="lp-final-cta">
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-lime/15 blur-3xl" />
        <div className="mx-auto max-w-3xl px-5 text-center md:px-8">
          <Reveal>
            <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl lg:text-4xl">
              {lp.finalTitle}
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-paper/70 md:text-base">
              {lp.finalSub || "不用填写表格。点击按钮打开 WhatsApp，告诉我们你卖什么、目前做到哪里 —— 团队会先了解你的情况。"}
            </p>
            <div className="mt-8">
              <button
                onClick={() => openWhatsApp(`lp-${slug}-final`, { text: lp.finalCta })}
                className="group inline-flex items-center gap-2 rounded-full bg-lime px-8 py-4 text-base font-semibold text-ink shadow-[0_8px_30px_rgba(201,242,75,0.35)] transition-transform hover:-translate-y-0.5"
                data-testid="lp-final-chat-button"
                data-location={`lp-${slug}-final`}
              >
                <MessageCircle size={17} className="transition-transform group-hover:-rotate-12" />
                {lp.finalCta}
              </button>
            </div>
            <p className="mt-4 text-xs text-paper/50">免费初步咨询</p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
